// Shared ProseMirror/TipTap rich-text helpers. Pure (no Firestore) so both the
// embertracking app and the hub can consume them via @embertracking/ui. Mirrors
// the originals in embertracking/src/data/lessonPlans.js, which now re-export from
// here to keep a single source of truth.

const EMPTY_DOC = {type: 'doc', content: [{type: 'paragraph'}]};

// Convert a value into a ProseMirror "doc" JSON shape. Plain strings get wrapped
// into paragraphs; existing rich-text docs pass through; null/undefined → empty doc.
export const ensureRichText = value => {
    if (value && typeof value === 'object' && value.type === 'doc') return value;
    if (typeof value === 'string') {
        const text = value.trim();
        if (!text) return EMPTY_DOC;
        return {
            type: 'doc',
            content: text.split('\n\n').map(paragraph => ({
                type: 'paragraph',
                content: paragraph ? [{type: 'text', text: paragraph}] : []
            }))
        };
    }
    return EMPTY_DOC;
};

// Cheap "is this rich-text doc / string empty?" check — used for hiding empty
// fields in readonly views.
export const isEmptyRichText = value => {
    if (!value) return true;
    if (typeof value === 'string') return value.trim().length === 0;
    if (typeof value !== 'object') return false;
    if (value.type !== 'doc') return false;
    const content = value.content || [];
    if (content.length === 0) return true;
    // Doc with a single empty paragraph (Tiptap's default empty state).
    if (content.length === 1) {
        const node = content[0];
        if (node.type === 'paragraph' && (!node.content || node.content.length === 0)) return true;
    }
    // Walk for any text/image content.
    let hasContent = false;
    const walk = node => {
        if (hasContent) return;
        if (node.type === 'text' && node.text && node.text.trim()) {
            hasContent = true;
            return;
        }
        if (node.type === 'image' && node.attrs?.src) {
            hasContent = true;
            return;
        }
        for (const child of node.content || []) walk(child);
    };
    walk(value);
    return !hasContent;
};

// Flatten a ProseMirror doc (or plain string) to plain text — used for previews
// where rendering the rich JSON would error (e.g. autocomplete option descriptions).
export const richTextToPlain = value => {
    if (!value) return '';
    if (typeof value === 'string') return value;
    if (typeof value !== 'object') return String(value);
    const out = [];
    const walk = node => {
        if (!node) return;
        if (node.type === 'text' && typeof node.text === 'string') {
            out.push(node.text);
            return;
        }
        if (node.type === 'hardBreak') {
            out.push(' ');
            return;
        }
        if (Array.isArray(node.content)) {
            for (const child of node.content) walk(child);
            // Block-level separator
            if (['paragraph', 'heading', 'listItem', 'blockquote'].includes(node.type)) {
                out.push(' ');
            }
        }
    };
    walk(value);
    return out.join('').replace(/\s+/g, ' ').trim();
};
